/**
 * scatterBuilder.ts
 * -------------------------------------------------------
 * 职责：根据园区数据 + 选中状态，构建 scatter3D 的 data & series。
 * -------------------------------------------------------
 */

import type * as echarts from 'echarts';
import { PARK_COLOR_PENDING } from '../../../constants/parkLevelColors';
import type { Park } from '../../../types';
import { SCATTER_ALTITUDE } from '../mapConstants';
import type { ScatterDatum } from '../types';

/* -------- 颜色常量 -------- */

const COLOR_NATIONAL = '#1890ff';
const COLOR_PROVINCIAL = '#52c41a';

function levelColor(level: Park['parkLevel']): string {
    if (level === 'national') return COLOR_NATIONAL;
    if (level === 'pending') return PARK_COLOR_PENDING;
    return COLOR_PROVINCIAL;
}

/* -------- 单点样式决策 -------- */

interface PointColors { color: string; opacity: number }

/**
 * 根据「当前点」和「选中点」的关系决定颜色 & 透明度。
 * 规则：
 *   - 无选中 → 原色全透明
 *   - 选中自己 → 红色
 *   - 选中国家级 → 同级变淡，其他不变
 *   - 选中省级/待开发 → 同省同级变淡
 */
function resolvePointColors(
    p: Park,
    selectedId: string | null,
    selectedPark: Park | undefined,
): PointColors {
    if (!selectedId || !selectedPark) {
        return { color: levelColor(p.parkLevel), opacity: 1 };
    }
    if (selectedId === p.id) {
        return { color: '#ff4d4f', opacity: 1 };
    }
    if (selectedPark.parkLevel === 'national') {
        return {
            color: levelColor(p.parkLevel),
            opacity: p.parkLevel === 'national' ? 0.3 : 1,
        };
    }
    // 选中省级/待开发 → 同省同级变淡
    if (p.parkLevel !== 'national') {
        const sameProvince = p.province === selectedPark.province && p.id !== selectedPark.id;
        const bothSubNational =
            (p.parkLevel === 'provincial' || p.parkLevel === 'pending') &&
            (selectedPark.parkLevel === 'provincial' || selectedPark.parkLevel === 'pending');
        if (sameProvince && bothSubNational) {
            return { color: levelColor(p.parkLevel), opacity: 0.3 };
        }
    }
    return { color: levelColor(p.parkLevel), opacity: 1 };
}

/* -------- 公共函数 -------- */

/** 将园区列表 → scatter3D 可用的 data 数组 */
export function buildScatterData(
    parks: Park[],
    selectedParkId: string | null,
): ScatterDatum[] {
    const selected = selectedParkId
        ? parks.find((x) => x.id === selectedParkId)
        : undefined;

    return parks.map((p) => {
        const isSelected = selectedParkId === p.id;
        const baseSize = p.parkLevel === 'national' ? 9.5 : p.parkLevel === 'pending' ? 8.5 : 8;
        const { color, opacity } = resolvePointColors(p, selectedParkId, selected);

        return {
            value: [p.longitude, p.latitude, SCATTER_ALTITUDE],
            name: p.name,
            parkId: p.id,
            parkLevel: p.parkLevel,
            pendingApproval: undefined,
            symbolSize: isSelected ? baseSize + 4 : baseSize,
            itemStyle: {
                color,
                opacity,
                borderColor: isSelected ? '#fff2f0' : 'rgba(0, 184, 217, 0.65)',
                borderWidth: isSelected ? 2 : 1.5,
            },
        };
    });
}

/** 构建 scatter3D series 配置 */
export function buildScatterSeries(
    data: ScatterDatum[],
): echarts.SeriesOption {
    return {
        type: 'scatter3D',
        name: 'parks',
        coordinateSystem: 'geo3D',
        symbol: 'circle',
        data,
        tooltip: { show: true },
        label: {
            show: true,
            formatter: '',
            position: 'top',
            distance: 4,
            color: '#ffccc7',
            fontWeight: 600,
        },
        emphasis: {
            disabled: true,
            label: { show: false, formatter: '' },
            itemStyle: { borderWidth: 2, borderColor: '#ffffff' },
        },
        blur: { label: { show: false, formatter: '' } },
        select: { label: { show: false, formatter: '' } },
    } as unknown as echarts.SeriesOption;
}
